import { computed, type ComputedRef, type DeepReadonly, onUnmounted, readonly, ref, type Ref } from "vue";

const MAX_ZOOM = 4;
const ZOOM_STEP = 0.5;

export interface ImageZoom {
	steps: readonly number[];
	level: DeepReadonly<Ref<number>>;
	x: DeepReadonly<Ref<number>>;
	y: DeepReadonly<Ref<number>>;
	dragging: DeepReadonly<Ref<boolean>>;
	zoom: ComputedRef<number>;
	zoomed: ComputedRef<boolean>;
	zoomLabel: ComputedRef<string>;
	transform: ComputedRef<string>;
	cursor: ComputedRef<string>;
	setViewport: (el: HTMLElement | null) => void;
	/**
	 * Tell the composable the natural (unscaled) size of the rendered content so pan limits clamp
	 * to the actually-visible image box, not the outer viewport (LSP-17655 review #4) — a
	 * letterboxed/portrait image inside a wider `object-fit: contain` viewport displays smaller
	 * than the container, so clamping to the container lets the user pan well past its edges.
	 * Pass invalid/non-positive dimensions (e.g. while the image hasn't loaded yet) to clear the
	 * content size and fall back to clamping against the viewport rect.
	 */
	setContentSize: (width: number, height: number) => void;
	setContentMaxHeight: (pixels: number | null) => void;
	zoomIn: () => void;
	zoomOut: () => void;
	reset: () => void;
	stepOnDoubleClick: () => void;
	onPanStart: (event: PointerEvent) => void;
	/** Re-clamp the current pan offset to the viewport's current pan limits (e.g. after a viewport resize). */
	reclamp: () => void;
}

function buildSteps(): number[] {
	const out = [1];
	for (let value = 1 + ZOOM_STEP; value <= MAX_ZOOM + 1e-6; value += ZOOM_STEP) {
		out.push(Math.round(value * 100) / 100);
	}
	return out;
}

export function useImageZoom(): ImageZoom {
	const steps = Object.freeze(buildSteps());
	const levelRef = ref(0);
	const level = readonly(levelRef);
	// Internal mutable refs — only this composable is allowed to write to pan/drag state.
	// Externally exposed as readonly() below (LSP-17655 review #5) so callers can't desync
	// the pan offset from the clamp invariants that setLevel/onPanStart maintain.
	const xRef = ref(0);
	const yRef = ref(0);
	const draggingRef = ref(false);
	const x = readonly(xRef);
	const y = readonly(yRef);
	const dragging = readonly(draggingRef);

	let viewport: HTMLElement | null = null;
	// Natural (unscaled) content dimensions (LSP-17655 review #4) — null until ZoomablePhoto reports
	// them (e.g. on the image's load event), in which case panLimits falls back to the viewport rect.
	let contentWidth: number | null = null;
	let contentHeight: number | null = null;
	// LSP-19030: the max-height cap the parent applies to the rendered image at mobile breakpoints,
	// in px. The image's real box is `min(viewportRect.height, cap)`, so a cap the clamp cannot see
	// makes panLimits too generous and lets the user drag the photo past its own edge into black.
	// Null means uncapped (every desktop breakpoint).
	let contentMaxHeight: number | null = null;
	let moveHandler: ((event: PointerEvent) => void) | null = null;
	let upHandler: ((event: PointerEvent) => void) | null = null;
	let cancelHandler: ((event: PointerEvent) => void) | null = null;
	// Identity of the pointer that started the current drag (LSP-17655 review #2) — a second
	// finger/pointer landing mid-drag must not be able to hijack or prematurely end the pan.
	let activePointerId: number | null = null;

	const zoom = computed(() => steps[Math.min(level.value, steps.length - 1)]);
	const zoomed = computed(() => zoom.value > 1);
	const zoomLabel = computed(() => `${Math.round(zoom.value * 100)}%`);
	const transform = computed(() => (zoomed.value ? `translate(${xRef.value}px, ${yRef.value}px) scale(${zoom.value})` : "none"));
	const cursor = computed(() => (zoomed.value ? (draggingRef.value ? "grabbing" : "grab") : "default"));

	function clamp(value: number, limit: number): number {
		const safeLimit = Number.isFinite(limit) && limit > 0 ? limit : 0;
		const clamped = Math.max(-safeLimit, Math.min(safeLimit, value));
		return clamped === 0 ? 0 : clamped;
	}

	/**
	 * The rendered (contained) content box within the viewport rect, given the known natural
	 * content aspect ratio. Falls back to the full viewport box when the content size is unknown
	 * or degenerate (LSP-17655 review #4).
	 */
	function contentBox(rectWidth: number, rectHeight: number): { width: number; height: number } {
		// The cap bounds the box the image is contained INTO, so it applies whether or not the natural
		// content size is known yet (LSP-19030, break-it round 4).
		const availableHeight = contentMaxHeight === null ? rectHeight : Math.min(rectHeight, contentMaxHeight);
		if (contentWidth === null || contentHeight === null) return { width: rectWidth, height: availableHeight };
		const aspect = contentWidth / contentHeight;
		if (!Number.isFinite(aspect) || aspect <= 0) return { width: rectWidth, height: availableHeight };
		return { width: Math.min(rectWidth, availableHeight * aspect), height: Math.min(availableHeight, rectWidth / aspect) };
	}

	function panLimits(scale: number): { lx: number; ly: number } {
		if (!viewport || typeof viewport.getBoundingClientRect !== "function") return { lx: 0, ly: 0 };
		const rect = viewport.getBoundingClientRect();
		if (!rect || !Number.isFinite(rect.width) || !Number.isFinite(rect.height) || rect.width <= 0 || rect.height <= 0) {
			return { lx: 0, ly: 0 };
		}
		const { width, height } = contentBox(rect.width, rect.height);
		return {
			lx: Math.max(0, (width * scale - rect.width) / 2),
			ly: Math.max(0, (height * scale - rect.height) / 2)
		};
	}

	function setLevel(next: number): void {
		levelRef.value = Math.max(0, Math.min(steps.length - 1, next));
		if (levelRef.value === 0) {
			xRef.value = 0;
			yRef.value = 0;
			if (draggingRef.value) stopDrag();
			return;
		}
		const { lx, ly } = panLimits(zoom.value);
		xRef.value = clamp(xRef.value, lx);
		yRef.value = clamp(yRef.value, ly);
	}

	function detach(): void {
		if (moveHandler) window.removeEventListener("pointermove", moveHandler);
		if (upHandler) window.removeEventListener("pointerup", upHandler);
		if (cancelHandler) window.removeEventListener("pointercancel", cancelHandler);
		moveHandler = null;
		upHandler = null;
		cancelHandler = null;
		activePointerId = null;
	}

	function stopDrag(): void {
		draggingRef.value = false;
		detach();
	}

	function onPanStart(event: PointerEvent): void {
		if (!zoomed.value) return;
		if (event.button !== 0) return;
		// LSP-17655 review #3: a second pointerdown (e.g. a second finger) while a drag is already
		// active must not tear down and hijack the first pointer's pan — ignore it and let the
		// active drag continue uninterrupted.
		if (draggingRef.value && activePointerId !== null && event.pointerId !== activePointerId) return;
		detach();
		// LSP-17655 review #2 (HIGH): calling preventDefault() on a TOUCH pointerdown cancels the
		// browser's synthesized mouse-compat sequence (mousedown → … → click → dblclick), which would
		// permanently kill double-tap-to-zoom on touch devices after the first zoom-in. On touch the
		// viewport already sets `touch-action: none` while zoomed, so native panning/scrolling is
		// suppressed without preventDefault; only mouse/pen need it (to stop native image-drag and
		// text-selection during a pan).
		if (event.pointerType !== "touch") event.preventDefault();
		activePointerId = event.pointerId;
		const startX = event.clientX - xRef.value;
		const startY = event.clientY - yRef.value;
		draggingRef.value = true;
		moveHandler = (moveEvent: PointerEvent) => {
			if (moveEvent.pointerId !== activePointerId) return;
			if (!zoomed.value || moveEvent.buttons === 0) {
				stopDrag();
				return;
			}
			const { lx, ly } = panLimits(zoom.value);
			xRef.value = clamp(moveEvent.clientX - startX, lx);
			yRef.value = clamp(moveEvent.clientY - startY, ly);
		};
		const endDrag = (endEvent: PointerEvent) => {
			if (endEvent.pointerId !== activePointerId) return;
			stopDrag();
		};
		upHandler = endDrag;
		cancelHandler = endDrag;
		window.addEventListener("pointermove", moveHandler);
		window.addEventListener("pointerup", upHandler);
		window.addEventListener("pointercancel", cancelHandler);
	}

	function reclamp(): void {
		if (!zoomed.value) return;
		const { lx, ly } = panLimits(zoom.value);
		xRef.value = clamp(xRef.value, lx);
		yRef.value = clamp(yRef.value, ly);
	}

	function setContentSize(width: number, height: number): void {
		if (Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0) {
			contentWidth = width;
			contentHeight = height;
		} else {
			contentWidth = null;
			contentHeight = null;
		}
		reclamp();
	}

	// LSP-19030 break-it round 4: keeps the pan clamp honest while a mobile height cap is active.
	function setContentMaxHeight(pixels: number | null): void {
		contentMaxHeight = pixels !== null && Number.isFinite(pixels) && pixels > 0 ? pixels : null;
		reclamp();
	}

	onUnmounted(detach);

	return {
		steps,
		level,
		x,
		y,
		dragging,
		zoom,
		zoomed,
		zoomLabel,
		transform,
		cursor,
		setViewport: (el) => {
			viewport = el;
			const { lx, ly } = panLimits(zoom.value);
			xRef.value = clamp(xRef.value, lx);
			yRef.value = clamp(yRef.value, ly);
		},
		setContentSize,
		setContentMaxHeight,
		zoomIn: () => setLevel(level.value + 1),
		zoomOut: () => setLevel(level.value - 1),
		reset: () => setLevel(0),
		stepOnDoubleClick: () => setLevel(level.value >= steps.length - 1 ? 0 : level.value + 1),
		onPanStart,
		reclamp
	};
}
